import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { parseVideoLink } from '../../../lib/video-source'

export const VIDEO_PREVIEW_EMPTY_COPY = 'Enter a video link to preview here'

type Props = Readonly<{
  videoUrl: string
  testId?: string
  minHeight?: number
}>

/** Inline YouTube / Vimeo embed or MP4 player — no external navigation. */
export function VideoPlaybackEmbed({ videoUrl, testId = 'video-playback', minHeight = 180 }: Props) {
  const parsed = parseVideoLink(videoUrl)

  if (!parsed) {
    return (
      <Box
        data-testid={`${testId}-empty`}
        sx={{
          minHeight,
          borderRadius: '10px',
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2,
        }}
      >
        <Typography sx={{ fontSize: '14px', lineHeight: '21px', color: 'text.secondary', textAlign: 'center' }}>
          {VIDEO_PREVIEW_EMPTY_COPY}
        </Typography>
      </Box>
    )
  }

  if (parsed.kind === 'mp4') {
    return (
      <Box
        data-testid={`${testId}-mp4`}
        sx={{
          width: '100%',
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: '#000',
          minHeight,
        }}
      >
        <Box
          component="video"
          src={parsed.src}
          controls
          playsInline
          preload="metadata"
          sx={{ width: '100%', display: 'block', maxHeight: 360 }}
        />
      </Box>
    )
  }

  return (
    <Box
      data-testid={`${testId}-${parsed.kind}`}
      sx={{
        position: 'relative',
        width: '100%',
        pt: '56.25%',
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: '#000',
      }}
    >
      <Box
        component="iframe"
        title="Video"
        src={parsed.embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    </Box>
  )
}
